/**
 * RoleWizardLayout.jsx — Employer v1.1 Phase 0.6 Slice B.
 *
 * Outer chrome for the draft authoring wizard:
 *   - 4-step stepper (Step 4 "Review & publish" is rendered LOCKED in
 *     Slice B — Slice C will activate it)
 *   - save indicator badge (Saving… / Saved · just now / Saved · 3m ago /
 *     Unsaved changes / Save failed — retry)
 *   - explicit "Save draft" button bypassing the debounce
 *   - "Back to roles" + step navigation
 *   - stale-tab modal (W-1)
 *
 * Pure presentation: receives state + callbacks via props.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, Check, ChevronLeft, ChevronRight,
  Loader2, Lock, Save,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import RoleStatusBadge from './RoleStatusBadge';

export const WIZARD_STEPS = [
  { n: 1, key: 'basics',           title: 'Basics',                  enabled: true },
  { n: 2, key: 'responsibilities', title: 'Responsibilities & skills', enabled: true },
  { n: 3, key: 'criteria',         title: 'Criteria & dealbreakers', enabled: true },
  // Phase 0.6 Slice C — Step 4 unlocks once the draft exists. The
  // wizard layout consumer (`roleId` truthy) flips this enabled at
  // render time; the static default below remains `true` so source
  // pins can pin the unlocked vocabulary.
  { n: 4, key: 'review',           title: 'Review & publish',        enabled: true },
];

function fmtAge(ts) {
  if (!ts) return '';
  const now = Date.now();
  const t   = new Date(ts).getTime();
  if (Number.isNaN(t)) return '';
  const delta = Math.max(0, Math.round((now - t) / 1000));
  if (delta < 5)  return 'just now';
  if (delta < 60) return `${delta}s ago`;
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  return `${Math.floor(delta / 3600)}h ago`;
}

function SaveIndicator({ status, lastSavedAt, error }) {
  const [, setTick] = useState(0);
  // Re-render every 30 s so "Saved · 3m ago" keeps current. Lightweight.
  useEffect(() => {
    const i = setInterval(() => setTick((x) => x + 1), 30 * 1000);
    return () => clearInterval(i);
  }, []);

  if (status === 'saving') {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-slate-300"
        data-testid="wizard-save-indicator-saving"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving…
      </span>
    );
  }
  if (status === 'pending') {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-amber-200"
        data-testid="wizard-save-indicator-pending"
      >
        Unsaved changes
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-rose-200"
        title={error || ''}
        data-testid="wizard-save-indicator-error"
      >
        <AlertTriangle className="h-3 w-3" />
        Save failed — retry
      </span>
    );
  }
  if (status === 'saved' && lastSavedAt) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-emerald-200"
        title={new Date(lastSavedAt).toISOString()}
        data-testid="wizard-save-indicator-saved"
      >
        <Check className="h-3 w-3" />
        Saved · {fmtAge(lastSavedAt)}
      </span>
    );
  }
  return (
    <span
      className="text-xs text-slate-300"
      data-testid="wizard-save-indicator-idle"
    >
      No changes yet
    </span>
  );
}

export default function RoleWizardLayout({
  step,
  onStepChange,
  roleId,
  roleTitle,
  status,
  saveStatus,
  saveError,
  lastSavedAt,
  dirty,
  onSaveDraft,
  staleTab,
  onReloadStale,
  children,
}) {
  const navigate = useNavigate();
  const stepIdx  = Math.max(0, Math.min(WIZARD_STEPS.length - 1, (step || 1) - 1));
  const current  = WIZARD_STEPS[stepIdx];
  const prevStep = stepIdx > 0 ? WIZARD_STEPS[stepIdx - 1] : null;
  const nextStep = stepIdx < WIZARD_STEPS.length - 1 ? WIZARD_STEPS[stepIdx + 1] : null;

  return (
    <div className="space-y-6 p-6" data-testid="role-wizard-layout">
      {/* Header row: back + title + status + save indicator */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate('/employer/roles')}
          data-testid="wizard-back-to-roles"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to roles
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <SaveIndicator
            status={saveStatus}
            error={saveError}
            lastSavedAt={lastSavedAt}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={onSaveDraft}
            disabled={!dirty || saveStatus === 'saving'}
            data-testid="wizard-save-draft"
          >
            <Save className="mr-2 h-4 w-4" />
            Save draft
          </Button>
        </div>
      </div>

      {/* Title + status */}
      <header className="space-y-1">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold text-ndor-ink">
            {roleId ? (roleTitle || '(untitled)') : 'Create role'}
          </h1>
          {status ? <RoleStatusBadge status={status} /> : null}
        </div>
        <p className="text-sm text-slate-300">
          Draft authoring · Steps 1–3. Publishing arrives in the next slice.
        </p>
      </header>

      {/* Stepper */}
      <ol
        className="flex flex-wrap items-center gap-2"
        data-testid="wizard-stepper"
      >
        {WIZARD_STEPS.map((s, i) => {
          const isActive = i === stepIdx;
          const isDone   = i < stepIdx;
          // Phase 0.6 Slice C — Step 4 (review) only unlocks once
          // the draft exists. Steps 2-3 already require roleId via
          // their content (criteria PATCH is row-scoped).
          const stepEnabled = s.enabled && !(s.key === 'review' && !roleId);
          return (
            <li key={s.key} className="flex items-center gap-2">
              <button
                type="button"
                disabled={!stepEnabled}
                onClick={() => stepEnabled && onStepChange(s.n)}
                className={
                  'rounded-full border px-3 py-1.5 text-sm transition-colors ' +
                  (isActive
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : isDone
                      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                      : 'border-slate-200 bg-ndor-surface text-slate-300 hover:bg-ndor-surface-alt') +
                  (!stepEnabled ? ' opacity-50 cursor-not-allowed' : '')
                }
                data-testid={`wizard-step-tab-${s.key}`}
                title={
                  !stepEnabled && s.key === 'review'
                    ? 'Save the draft first to enable Review & publish'
                    : undefined
                }
              >
                <span className="font-mono mr-2">{s.n}</span>
                {s.title}
                {!stepEnabled ? <Lock className="inline ml-2 h-3 w-3" /> : null}
              </button>
              {i < WIZARD_STEPS.length - 1 ? (
                <span aria-hidden className="text-slate-300">·</span>
              ) : null}
            </li>
          );
        })}
      </ol>

      {/* Body */}
      <div
        className="rounded-md border border-slate-200 bg-ndor-surface"
        data-testid={`wizard-step-body-${current.key}`}
      >
        {children}
      </div>

      {/* Footer nav */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          size="sm"
          variant="ghost"
          disabled={!prevStep || !prevStep.enabled}
          onClick={() => prevStep && onStepChange(prevStep.n)}
          data-testid="wizard-prev-step"
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          {prevStep ? prevStep.title : 'Previous'}
        </Button>
        <Button
          size="sm"
          disabled={
            !nextStep ||
            (nextStep.key === 'review' && !roleId) ||
            (!roleId && current.n === 1 && !dirty)
          }
          onClick={() => {
            if (!nextStep) return;
            if (nextStep.key === 'review' && !roleId) return;
            onStepChange(nextStep.n);
          }}
          title={
            nextStep && nextStep.key === 'review' && !roleId
              ? 'Save the draft first to enable Review & publish'
              : undefined
          }
          data-testid="wizard-next-step"
        >
          {nextStep ? nextStep.title : 'Finish'}
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {/* Stale-tab modal (W-1) */}
      <Dialog
        open={Boolean(staleTab)}
        onOpenChange={(open) => { if (!open) onReloadStale(); }}
      >
        <DialogContent data-testid="wizard-stale-tab-modal">
          <DialogHeader>
            <DialogTitle>This role was edited elsewhere</DialogTitle>
            <DialogDescription>
              We detected a newer version of this role on the server.
              Reload to pick up the latest changes before continuing.
              Your unsaved edits in this tab will be discarded.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={onReloadStale}
              data-testid="wizard-stale-tab-reload"
            >
              Reload role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
